// AppSwitcher — pill that lets a logged-in user jump between FassalkartAI and SeedMap AI
// Shows in both Navbar (FassalkartAI portal) and SeedMapLayout (SeedMap AI portal)
// If user doesn't yet have the other portal → takes them to register

import React from 'react';
import { Box, Typography, Tooltip, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const APPS = [
  {
    id: 'fassalkart',
    label: 'FassalkartAI',
    shortLabel: 'Fassalkart',
    emoji: '🌾',
    path: '/dashboard',
    registerPath: '/register',
    activeBg: 'linear-gradient(135deg, #2E7D32 0%, #43A047 100%)',
    activeColor: '#fff',
    hoverBg: 'rgba(46,125,50,0.12)',
    hoverColor: '#2E7D32',
  },
  {
    id: 'seedmap',
    label: 'SeedMap AI',
    shortLabel: 'SeedMap',
    emoji: '🌱',
    path: '/business',
    registerPath: '/business/register',
    activeBg: 'linear-gradient(135deg, #E65100 0%, #F57C00 100%)',
    activeColor: '#fff',
    hoverBg: 'rgba(245,124,0,0.10)',
    hoverColor: '#E65100',
  },
];

/**
 * @param {'fassalkart'|'seedmap'} currentApp  which portal is active right now
 * @param {'light'|'dark'} variant              pill colour scheme (default 'light')
 */
export default function AppSwitcher({ currentApp, variant = 'light' }) {
  const navigate     = useNavigate();
  const { currentUser, hasFarmerProfile, hasCompanyProfile } = useAuth();

  if (!currentUser) return null;

  const hasAccess = { fassalkart: hasFarmerProfile, seedmap: hasCompanyProfile };
  const isDark    = variant === 'dark';

  const pillBg    = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)';
  const inactiveBg= isDark ? 'transparent'            : 'transparent';
  const inactiveColor = isDark ? 'rgba(255,255,255,0.65)' : '#555';

  const handleClick = (app) => {
    if (app.id === currentApp) return;
    if (hasAccess[app.id]) {
      navigate(app.path);
    } else {
      navigate(app.registerPath);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: pillBg,
        borderRadius: '12px',
        p: '3px',
        gap: '2px',
        flexShrink: 0,
      }}
    >
      {APPS.map((app) => {
        const isActive  = app.id === currentApp;
        const hasPortal = hasAccess[app.id];

        return (
          <Tooltip
            key={app.id}
            title={
              isActive
                ? `Currently on ${app.label}`
                : hasPortal
                  ? `Switch to ${app.label}`
                  : `Register for ${app.label}`
            }
            arrow
            placement="bottom"
          >
            <Box
              onClick={() => handleClick(app)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                px: { xs: 1.2, sm: 1.5 },
                py: 0.65,
                borderRadius: '9px',
                cursor: isActive ? 'default' : 'pointer',
                transition: 'all 0.18s ease',
                userSelect: 'none',
                background: isActive ? app.activeBg : inactiveBg,
                color:   isActive ? app.activeColor : inactiveColor,
                boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                opacity: (!hasPortal && !isActive) ? 0.72 : 1,
                '&:hover': !isActive ? {
                  bgcolor: app.hoverBg,
                  color:   app.hoverColor,
                } : {},
              }}
            >
              <Typography sx={{ fontSize: { xs: 14, sm: 15 }, lineHeight: 1 }}>
                {app.emoji}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 11, sm: 12.5 },
                  fontWeight: isActive ? 700 : 500,
                  fontFamily: 'Poppins, sans-serif',
                  lineHeight: 1,
                  display: { xs: 'none', sm: 'block' },
                  whiteSpace: 'nowrap',
                }}
              >
                {app.shortLabel}
              </Typography>

              {/* Badge for apps the user doesn't have yet */}
              {!hasPortal && !isActive && (
                <Chip
                  label="+"
                  size="small"
                  sx={{
                    height: 14, fontSize: 9, fontWeight: 700,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'inherit',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
